import {
  DoubleRightOutlined,
  DownloadOutlined,
  ExportOutlined,
  SearchOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Input,
  InputNumber,
  Popover,
  Select,
  Space,
  Table,
  TableColumnType,
  Upload,
} from "antd";
import { debounce } from "lodash";
import React, { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import ModalPreviewExportTransfer from "../../components/modal/Warehouse/ModalPreviewExportTransfer";
import {
  IPartNumberDtoExportTransfer,
  IPartNumberDtoResponse,
  IWarehouseLocation,
} from "../../models/Warehouse";
import {
  useImportWarehouseExportTransfer,
  useWarehouses,
} from "../../query/WarehouseQuery";
import { RootState } from "../../store";
import { renderQCStatusTag } from "../../utils/qcHelper";
import { notify } from "../../utils/toast";

const TransferWarehouseExport: React.FC = () => {
  const [searchParams, setSearchParams] = useState<{
    awb?: string;
    partNumber?: string;
  }>({});
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [warehouseFilter, setWarehouseFilter] = useState<string | undefined>();
  const locations = useSelector(
    (state: RootState) => state.warehouse.locations,
  ) as IWarehouseLocation[];
  const importMutation = useImportWarehouseExportTransfer();
  const { data: warehouseData, isLoading } = useWarehouses({
    page: currentPage,
    limit: pageSize,
    ...searchParams,
    code: warehouseFilter,
  });
  const filterOptions = locations
    .filter((item) => item.warehouseCode !== "QC")
    .map((item) => ({
      label: item.warehouseCode,
      value: item.warehouseCode,
    }));
  const [selectedItems, setSelectedItems] = useState<
    IPartNumberDtoExportTransfer[]
  >([]);
  const [previewVisible, setPreviewVisible] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchChange = useCallback(
    debounce((field: string, value: string) => {
      setSearchParams((prev) => ({
        ...prev,
        [field]: value || undefined,
      }));
      setCurrentPage(1);
    }, 300),
    [],
  );

  const handleExcelUpload = async (file: File) => {
    await importMutation.mutateAsync(file, {
      onSuccess: (data) => {
        if (data && Array.isArray(data)) {
          const processedItems = data.map((item) => ({
            ...item,
          }));

          setSelectedItems(processedItems);
          setPreviewVisible(true);
          notify.success(
            `Đã xử lý ${processedItems.length} mã hàng từ file Excel`,
          );
        } else {
          notify.error("Định dạng dữ liệu không hợp lệ");
        }
      },
      onError: (error: any) => {
        if (error.isAxiosError) {
          const serverMessage = error.response?.data?.message;
          notify.error(
            serverMessage || "Có lỗi khi xử lý file. Vui lòng thử lại.",
          );
        } else {
          notify.error("Kiểm tra lại thông tin dữ liệu xuất kho và thử lại");
        }
      },
    });
    return false;
  };

  const handlePreview = () => {
    if (selectedItems.length === 0) {
      return;
    }

    const invalidItems = selectedItems.filter((item) => {
      return (
        !item.targetProject || !item.targetWarehouseId || !item.targetLocationId
      );
    });

    if (invalidItems.length > 0) {
      notify.error("Vui lòng điền đầy đủ thông tin trước khi xem trước");
      return;
    }
    setPreviewVisible(true);
  };

  const handleExportSubmit = async () => {
    try {
      setPreviewVisible(false);
      setSelectedItems([]);
    } catch (error) {}
  };

  const columns: TableColumnType<IPartNumberDtoResponse>[] = [
    // {
    //   title: "Ngày",
    //   dataIndex: "importDate",
    //   key: "date",
    //   width: 100,
    //   minWidth: 100,
    //   align: "center",
    // },
    {
      title: "AWB",
      dataIndex: ["awb"],
      key: "awb",
      width: 110,
      minWidth: 70,
      align: "center",
      filterDropdown: () => {
        return (
          <div style={{ padding: 8 }}>
            <Input
              placeholder="Tìm kiếm AWB"
              onChange={(e) => {
                handleSearchChange("awb", e.target.value);
              }}
              style={{ marginBottom: 8, display: "block" }}
            />
          </div>
        );
      },
      filterIcon: () => (
        <SearchOutlined
          style={{ color: searchParams.awb ? "#1890ff" : undefined }}
        />
      ),
    },
    {
      title: "Mã kho",
      dataIndex: ["location", "warehouse", "warehouseCode"],
      key: "warehouseCode",
      width: 70,
      minWidth: 70,
      align: "center",
    },
    {
      title: "Dự án",
      dataIndex: "project",
      key: "project",
      width: 80,
      minWidth: 80,
      align: "center",
    },
    {
      title: "Mã hàng",
      dataIndex: "partNumber",
      key: "partNumber",
      width: 120,
      minWidth: 120,
      filterDropdown: () => {
        return (
          <div style={{ padding: 8 }}>
            <Input
              placeholder="Tìm kiếm mã hàng"
              onChange={(e) => {
                handleSearchChange("partNumber", e.target.value);
              }}
              style={{ marginBottom: 8, display: "block" }}
            />
          </div>
        );
      },
      filterIcon: () => (
        <SearchOutlined
          style={{ color: searchParams.partNumber ? "#1890ff" : undefined }}
        />
      ),
    },
    {
      title: "Mã dự án",
      dataIndex: "projectCode",
      key: "projectCode",
      width: 110,
      minWidth: 110,
    },
    {
      title: "QC Status",
      dataIndex: "qcStatus",
      key: "qcStatus",
      width: 120,
      minWidth: 120,
      render: (status: string) => renderQCStatusTag(status),
    },
    {
      title: "Số lượng",
      dataIndex: "stock",
      key: "stock",
      width: 100,
      minWidth: 100,
    },
    {
      title: "Đơn vị tính",
      dataIndex: "unit",
      key: "unit",
      width: 100,
      minWidth: 100,
    },
    {
      title: "Location",
      dataIndex: ["location", "code"],
      key: "location",
      width: 100,
      minWidth: 100,
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      minWidth: 100,
    },
  ];

  const handleAddToExport = (record: any) => {
    if (
      selectedItems.length > 0 &&
      record.project !== selectedItems[0].project
    ) {
      notify.error("Chỉ có thể chọn các mã hàng cùng dự án.");
      return;
    }
    if (selectedItems.length > 0) {
      const existingCode = selectedItems[0].location?.warehouse?.warehouseCode;
      const newCode = record.location?.warehouse?.warehouseCode;
      if (!validateWarehouseTransfer(existingCode, newCode)) {
        return;
      }
    }
    if (!selectedItems.find((item) => item.id === record.id)) {
      setSelectedItems([...selectedItems, { ...record, exportQuantity: 1 }]);
    }
  };
  const handleRemoveFromExport = (recordId: number) => {
    const remaining = selectedItems.filter((item) => item.id !== recordId);
    setSelectedItems(remaining);
  };
  const handleQuantityChange = (recordId: number, quantity: number) => {
    setSelectedItems(
      selectedItems.map((item) =>
        item.id === recordId
          ? { ...item, exportQuantity: quantity || 0 }
          : item,
      ),
    );
  };
  const handleLocationChange = (recordId: number, location: number) => {
    setSelectedItems(
      selectedItems.map((item) =>
        item.id === recordId ? { ...item, targetLocationId: location } : item,
      ),
    );
  };
  const handleProjectChange = (recordId: number, project: string) => {
    setSelectedItems(
      selectedItems.map((item) =>
        item.id === recordId ? { ...item, targetProject: project } : item,
      ),
    );
  };
  const handleProjectCodeChange = (recordId: number, projectCode: string) => {
    setSelectedItems(
      selectedItems.map((item) =>
        item.id === recordId
          ? { ...item, targetProjectCode: projectCode }
          : item,
      ),
    );
  };
  const getValidTargetWarehouses = (sourceCode: string | undefined) => {
    if (!sourceCode) return [];
    const group = sourceCode.charAt(0);
    return filterOptions.filter((option) => option.value.charAt(0) === group);
  };

  const validateWarehouseTransfer = (
    sourceCode: string | undefined,
    targetCode: string,
  ): boolean => {
    if (!sourceCode || !targetCode) return false;
    const sourceGroup = sourceCode.charAt(0);
    const targetGroup = targetCode.charAt(0);
    if (sourceGroup !== targetGroup) {
      notify.error(
        `Không thể chuyển hàng từ kho ${sourceCode} sang kho ${targetCode}`,
      );
      return false;
    }
    return true;
  };

  const handleWarehouseChange = (recordId: number, warehouseCode: string) => {
    const item = selectedItems.find((item) => item.id === recordId);
    if (!item) return;

    const sourceCode = item.location?.warehouse?.warehouseCode;
    if (validateWarehouseTransfer(sourceCode, warehouseCode)) {
      const targetWarehouse = locations.find(
        (loc) => loc.warehouseCode === warehouseCode,
      );
      if (targetWarehouse) {
        setSelectedItems(
          selectedItems.map((item) =>
            item.id === recordId
              ? { ...item, targetWarehouseId: targetWarehouse.id }
              : item,
          ),
        );
      }
    }
  };

  const updatedColumns = columns.map((col) => {
    if (col.key === "action") {
      return {
        ...col,
        render: (_: any, record: any) => (
          <Button
            icon={<ExportOutlined />}
            onClick={() => handleAddToExport(record)}
            disabled={
              record.qcStatus === "PENDING" ||
              record.qcStatus === "REJECT" ||
              record.stock <= 0 ||
              selectedItems.some((item) => item.id === record.id) ||
              (selectedItems.length > 0 &&
                record.project !== selectedItems[0].project)
            }
          >
            Chọn
          </Button>
        ),
      };
    }
    return col;
  });

  return (
    <div style={{ maxHeight: "100vh" }}>
      <div className="flex gap-4 flex-wrap lg:flex-nowrap">
        <div className="flex-1 w-full min-w-[600px]">
          <Card size="small" className="mb-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 flex-wrap">
              <Space>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={() => {
                    const headers = [
                      "Dự án",
                      "Mã dự án",
                      "Mã hàng",
                      "AWB",
                      "Mã kho",
                      "Vị trí",
                      "Số lượng xuất kho",
                      "Dự án đích",
                      "Mã dự án đích",
                      "Mã kho đích",
                      "Vị trí đích",
                    ];
                    const exampleRow = {
                      "Dự án": "Dự án A",
                      "Mã dự án": "Example: PJ001",
                      "Mã hàng": "Example: ABC123",
                      AWB: "Example: AWB123456",
                      "Mã kho": "Example: TK2",
                      "Vị trí": "Example: TK2-01",
                      "Số lượng xuất kho": "Example: 10",
                      "Dự án đích": "Dự án B",
                      "Mã dự án đích": "Example: PJ002",
                      "Mã kho đích": "Example: TK3",
                      "Vị trí đích": "Example: Vị trí A1",
                    };
                    const ws = XLSX.utils.json_to_sheet([exampleRow], {
                      header: headers,
                    });
                    ws["!cols"] = headers.map(() => ({ wch: 20 }));
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(
                      wb,
                      ws,
                      "Xuất luân chuyển kho",
                    );
                    XLSX.writeFile(wb, "xuat_luan_chuyen_kho_template.xlsx");
                  }}
                >
                  Tải mẫu Excel
                </Button>
                <Upload
                  accept=".xlsx,.xls"
                  showUploadList={false}
                  beforeUpload={handleExcelUpload}
                  disabled={importMutation.isPending}
                >
                  <Button
                    loading={importMutation.isPending}
                    icon={<UploadOutlined />}
                  >
                    Nhập từ Excel
                  </Button>
                </Upload>
              </Space>
              <Popover
                title="Lọc theo mã kho"
                trigger="click"
                content={
                  <div style={{ padding: "8px" }}>
                    <Space direction="vertical">
                      <Space wrap>
                        {filterOptions.map((option) => (
                          <Button
                            key={option.value}
                            type={
                              warehouseFilter === option.value
                                ? "primary"
                                : "default"
                            }
                            onClick={() => {
                              setWarehouseFilter(
                                warehouseFilter === option.value
                                  ? undefined
                                  : option.value,
                              );
                              setCurrentPage(1);
                            }}
                            size="small"
                          >
                            {option.label}
                          </Button>
                        ))}
                      </Space>
                      {warehouseFilter && (
                        <Button
                          type="link"
                          danger
                          size="small"
                          onClick={() => {
                            setWarehouseFilter(undefined);
                            setCurrentPage(1);
                          }}
                        >
                          Xóa bộ lọc
                        </Button>
                      )}
                    </Space>
                  </div>
                }
              >
                <Button type={warehouseFilter ? "primary" : "default"}>
                  Mã kho {warehouseFilter ? `(${warehouseFilter})` : ""}
                </Button>
              </Popover>
            </div>
          </Card>

          <Table
            columns={updatedColumns}
            dataSource={warehouseData?.data || []}
            loading={isLoading}
            rowKey="id"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: warehouseData?.pagination?.total || 0,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, 50],
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              },
            }}
            scroll={{ y: 600 }}
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: 8,
              overflow: "hidden",
              width: "100%",
            }}
          />
        </div>

        <Card
          title="Danh sách điều chuyển"
          className="w-full w-2xl lg:max-w-xl"
          extra={
            <Button
              type="primary"
              disabled={selectedItems.length === 0}
              onClick={handlePreview}
            >
              <ExportOutlined />
              Preview
            </Button>
          }
        >
          <div className="space-y-4 max-h-[800px] overflow-y-auto">
            {selectedItems.map((item) => (
              <Card
                key={item.id}
                size="small"
                className="bg-[var(--bg-secondary)]"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="font-medium">PN : {item.partNumber}</div>
                    <div className="font-medium">AWB : {item.awb}</div>
                    <div className="text-sm text-gray-500">
                      PJ : {item.projectCode}
                    </div>
                  </div>
                  <Button
                    size="small"
                    danger
                    onClick={() => handleRemoveFromExport(item.id)}
                  >
                    Xóa
                  </Button>
                </div>
                <div className="flex items-stretch gap-6 mb-4">
                  <div className="flex-1 p-4 rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <div className="text-sm font-medium text-gray-600">
                        Vị trí hiện tại
                      </div>
                    </div>
                    <div className="space-y-3 ">
                      <div>
                        <div className="text-xs font-medium text-gray-500 mb-1.5">
                          Dự án
                        </div>
                        <Input
                          value={item.project}
                          size="middle"
                          disabled
                          className="bg-gray-50"
                        />
                      </div>
                      <div>
                        <div className="text-xs font-medium text-gray-500 mb-1.5">
                          Mã dự án
                        </div>
                        <Input
                          value={item.projectCode || "N/A"}
                          size="middle"
                          disabled
                          className="bg-gray-50"
                        />
                      </div>
                      <div>
                        <div className="text-xs font-medium text-gray-500 mb-1.5">
                          Mã kho
                        </div>
                        <Input
                          value={item.location?.warehouse?.warehouseCode}
                          size="middle"
                          disabled
                          className="bg-gray-50"
                        />
                      </div>
                      <div>
                        <div className="text-xs font-medium text-gray-500 mb-1.5">
                          Vị trí
                        </div>
                        <Input
                          value={item.location?.code}
                          size="middle"
                          disabled
                          className="bg-gray-50"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full border border-blue-100 flex items-center justify-center">
                      <span className="text-blue-500">
                        <DoubleRightOutlined />
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 p-4 rounded-lg border border-blue-100 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <div className="text-sm font-medium text-blue-600">
                        Điều chuyển đến
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <div className="text-xs font-medium text-gray-500 mb-1.5">
                          Dự án
                        </div>
                        <Input
                          onChange={(e) =>
                            handleProjectChange(item.id, e.target.value)
                          }
                          size="middle"
                          placeholder="Nhập dự án"
                          className="hover:border-blue-400 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <div className="text-xs font-medium text-gray-500 mb-1.5">
                          Mã dự án
                        </div>
                        <Input
                          onChange={(e) =>
                            handleProjectCodeChange(item.id, e.target.value)
                          }
                          size="middle"
                          placeholder="Nhập mã dự án"
                          className="hover:border-blue-400 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <div className="text-xs font-medium text-gray-500 mb-1.5">
                          Mã kho
                        </div>
                        <Select
                          size="middle"
                          style={{ width: "100%" }}
                          value={
                            locations.find(
                              (loc) => loc.id === item.targetWarehouseId,
                            )?.warehouseCode
                          }
                          onChange={(value) =>
                            handleWarehouseChange(item.id, value)
                          }
                          placeholder="Chọn mã kho"
                          className="hover:border-blue-400"
                        >
                          {getValidTargetWarehouses(
                            item.location?.warehouse?.warehouseCode,
                          ).map((option) => (
                            <Select.Option
                              key={option.value}
                              value={option.value}
                            >
                              {option.label}
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-gray-500 mb-1.5">
                          Vị trí
                        </div>
                        <Select
                          size="middle"
                          style={{ width: "100%" }}
                          placeholder="Chọn vị trí"
                          className="hover:border-blue-400"
                          value={item.targetLocationId}
                          onChange={(value) =>
                            handleLocationChange(item.id, value)
                          }
                        >
                          {locations
                            .filter((loc) => loc.id === item.targetWarehouseId)
                            .flatMap((loc) => loc.warehouseLocations)
                            .map((loc) => (
                              <Select.Option key={loc.id} value={loc.id}>
                                {loc.code}
                              </Select.Option>
                            ))}
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm">Số lượng xuất:</span>
                  <InputNumber
                    min={1}
                    max={item.stock}
                    value={item.exportQuantity}
                    onChange={(value) =>
                      handleQuantityChange(item.id, value || 0)
                    }
                    style={{ width: 100 }}
                  />
                  <span className="text-sm text-gray-500">
                    / {item.stock} {item.unit}
                  </span>
                </div>
              </Card>
            ))}
            {selectedItems.length === 0 && (
              <div className="text-center text-gray-500 py-4">
                Chưa có mã hàng nào được chọn
              </div>
            )}
          </div>
        </Card>
      </div>
      <ModalPreviewExportTransfer
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        onOk={handleExportSubmit}
        selectedItems={selectedItems}
      />
    </div>
  );
};
export default TransferWarehouseExport;
